<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { picoSearch } from '@scmmishra/pico-search';
import Avatar from 'next/avatar/Avatar.vue';
import { useAdmin } from 'dashboard/composables/useAdmin';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import {
  useMapGetter,
  useStoreGetters,
  useStore,
} from 'dashboard/composables/store';
import ChannelName from './components/ChannelName.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import InboxesAPI from 'dashboard/api/inboxes';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n({ useScope: 'global' });
const { isAdmin } = useAdmin();

const showDeletePopup = ref(false);
const selectedInbox = ref({});
const searchQuery = ref('');
const archivedInboxes = ref([]);
const hasArchivedInboxes = ref(false);
const isLoadingArchived = ref(false);

const inboxes = useMapGetter('inboxes/getInboxesForSettings');

const inboxesList = computed(() => {
  return inboxes.value?.slice().sort((a, b) => a.name.localeCompare(b.name));
});

const filteredInboxesList = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return inboxesList.value;
  return picoSearch(inboxesList.value, query, ['name', 'channel_type']);
});

const uiFlags = computed(() => getters['inboxes/getUIFlags'].value);

const deleteConfirmText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.YES')} ${selectedInbox.value.name}`
);

const deleteRejectText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.NO')} ${selectedInbox.value.name}`
);

const confirmDeleteMessage = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.MESSAGE')} ${selectedInbox.value.name}?`
);
const confirmPlaceHolderText = computed(
  () =>
    `${t('INBOX_MGMT.DELETE.CONFIRM.PLACE_HOLDER', {
      inboxName: selectedInbox.value.name,
    })}`
);

const deleteInbox = async ({ id }) => {
  try {
    await store.dispatch('inboxes/delete', id);
    useAlert(t('INBOX_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.DELETE.API.ERROR_MESSAGE'));
  }
};
const loadArchived = async () => {
  isLoadingArchived.value = true;
  try {
    const response = await InboxesAPI.getArchived();
    archivedInboxes.value = response.data.payload;
    hasArchivedInboxes.value = archivedInboxes.value.length > 0;
  } catch (error) {
    useAlert(t('INBOX_MGMT.DELETE.API.ERROR_MESSAGE'));
  } finally {
    isLoadingArchived.value = false;
  }
};
const restoreInbox = async inbox => {
  try {
    await InboxesAPI.restore(inbox.id);
    archivedInboxes.value = archivedInboxes.value.filter(
      item => item.id !== inbox.id
    );
    hasArchivedInboxes.value = archivedInboxes.value.length > 0;
    await store.dispatch('inboxes/get');
    useAlert(t('INBOX_MGMT.DELETE.API.RESTORE_SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.DELETE.API.RESTORE_ERROR_MESSAGE'));
  }
};
const checkArchivedInboxes = async () => {
  try {
    const response = await InboxesAPI.getArchived();
    hasArchivedInboxes.value = response.data.payload.length > 0;
  } catch {
    // Keep the archive action hidden when availability cannot be confirmed.
    // This avoids advertising an empty state; the next page visit retries.
    hasArchivedInboxes.value = false;
  }
};
const loadReconnectionRequired = async () => {
  try {
    const response = await InboxesAPI.getReconnectionRequired();
    response.data.payload.forEach(inbox => {
      store.commit('inboxes/EDIT_INBOXES', inbox);
    });
  } catch (error) {
    useAlert(t('INBOX_MGMT.DELETE.API.ERROR_MESSAGE'));
  }
};
const closeDelete = () => {
  showDeletePopup.value = false;
  selectedInbox.value = {};
};

const confirmDeletion = () => {
  deleteInbox(selectedInbox.value);
  closeDelete();
};
const openDelete = inbox => {
  showDeletePopup.value = true;
  selectedInbox.value = inbox;
};

onMounted(() => {
  loadReconnectionRequired();
  if (isAdmin.value) checkArchivedInboxes();
});
</script>

<template>
  <SettingsLayout
    :no-records-found="
      !inboxesList.length && !archivedInboxes.length && !hasArchivedInboxes
    "
    :no-records-message="$t('INBOX_MGMT.LIST.404')"
    :is-loading="uiFlags.isFetching"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('INBOX_MGMT.HEADER')"
        :description="$t('INBOX_MGMT.DESCRIPTION')"
        :link-text="$t('INBOX_MGMT.LEARN_MORE')"
        :search-placeholder="$t('INBOX_MGMT.SEARCH_PLACEHOLDER')"
        feature-name="inboxes"
      >
        <template v-if="inboxesList?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('INBOX_MGMT.COUNT', { n: inboxesList.length }) }}
          </span>
        </template>
        <template #actions>
          <Button
            v-if="isAdmin && hasArchivedInboxes"
            :label="$t('INBOX_MGMT.DELETE.SHOW_ARCHIVED')"
            outline
            size="sm"
            @click="loadArchived"
          />
          <router-link v-if="isAdmin" :to="{ name: 'settings_inbox_new' }">
            <Button :label="$t('SETTINGS.INBOXES.NEW_INBOX')" size="sm" />
          </router-link>
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <span
        v-if="!filteredInboxesList.length && searchQuery"
        class="flex-1 flex items-center justify-center py-20 text-center text-body-main !text-base text-n-slate-11"
      >
        {{ $t('INBOX_MGMT.NO_RESULTS') }}
      </span>
      <div v-else class="divide-y divide-n-weak border-t border-n-weak">
        <div
          v-for="inbox in filteredInboxesList"
          :key="inbox.id"
          class="flex justify-between flex-row items-start gap-4 py-4"
        >
          <div class="flex items-center gap-4">
            <div
              v-if="inbox.avatar_url"
              class="bg-n-alpha-3 rounded-xl size-10 ring ring-n-solid-1 border border-n-strong shadow-sm grid place-items-center"
            >
              <Avatar
                :src="inbox.avatar_url"
                :name="inbox.name"
                :size="24"
                rounded-full
              />
            </div>
            <div
              v-else
              class="size-10 justify-center bg-n-alpha-3 rounded-xl ring ring-n-solid-1 border border-n-strong shadow-sm grid place-items-center"
            >
              <ChannelIcon class="size-6 text-n-slate-10" :inbox="inbox" />
            </div>
            <div class="flex flex-col items-start gap-1">
              <span class="block text-heading-3 text-n-slate-12 capitalize">
                {{ inbox.name }}
              </span>
              <ChannelName
                :channel-type="inbox.channel_type"
                :medium="inbox.medium"
                :voice-enabled="inbox.voice_enabled"
                class="text-body-main text-n-slate-11"
              />
              <span
                v-if="inbox.phone_registration_required"
                class="text-xs text-n-amber-11"
              >
                {{ $t('INBOX_MGMT.WHATSAPP_RECEIVE_ONLY') }}
              </span>
            </div>
          </div>
          <div class="flex gap-3 justify-end">
            <router-link
              v-if="inbox.reconnection_required"
              :to="{
                name: 'settings_inbox_show',
                params: { inboxId: inbox.id },
              }"
            >
              <Button
                :label="$t('INBOX_MGMT.DELETE.RECONNECT_BUTTON_TEXT')"
                size="sm"
              />
            </router-link>
            <router-link
              v-else
              :to="{
                name: 'settings_inbox_show',
                params: { inboxId: inbox.id },
              }"
              :aria-label="`${$t('INBOX_MGMT.SETTINGS')}: ${inbox.name}`"
            >
              <Button
                v-if="isAdmin"
                v-tooltip.top="$t('INBOX_MGMT.SETTINGS')"
                icon="i-woot-settings"
                slate
                sm
                :aria-label="`${$t('INBOX_MGMT.SETTINGS')}: ${inbox.name}`"
              />
            </router-link>
            <Button
              v-if="isAdmin"
              v-tooltip.top="$t('INBOX_MGMT.DELETE.BUTTON_TEXT')"
              icon="i-woot-bin"
              slate
              sm
              class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
              :aria-label="`${$t('INBOX_MGMT.DELETE.BUTTON_TEXT')}: ${inbox.name}`"
              @click="openDelete(inbox)"
            />
          </div>
        </div>
      </div>
      <div
        v-if="archivedInboxes.length"
        class="mt-8 border-t border-n-weak pt-5"
      >
        <h3 class="text-heading-3 text-n-slate-12">
          {{ $t('INBOX_MGMT.DELETE.ARCHIVED_TITLE') }}
        </h3>
        <p class="mt-1 text-body-main text-n-slate-11">
          {{ $t('INBOX_MGMT.DELETE.ARCHIVED_DESCRIPTION') }}
        </p>
        <div class="mt-3 divide-y divide-n-weak">
          <div
            v-for="inbox in archivedInboxes"
            :key="inbox.id"
            class="flex items-center justify-between gap-4 py-3"
          >
            <div>
              <p class="text-body-main font-medium text-n-slate-12">
                {{ inbox.name }}
              </p>
              <p class="text-sm text-n-slate-11">{{ inbox.channel_type }}</p>
            </div>
            <Button
              :label="$t('INBOX_MGMT.DELETE.RESTORE_BUTTON_TEXT')"
              outline
              size="sm"
              @click="restoreInbox(inbox)"
            />
          </div>
        </div>
      </div>
      <p
        v-else-if="isLoadingArchived"
        class="mt-5 text-body-main text-n-slate-11"
      >
        {{ $t('INBOX_MGMT.DELETE.LOADING_ARCHIVED') }}
      </p>
    </template>

    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="$t('INBOX_MGMT.DELETE.CONFIRM.TITLE')"
      :message="confirmDeleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
      :confirm-value="selectedInbox.name"
      :confirm-place-holder-text="confirmPlaceHolderText"
      @on-confirm="confirmDeletion"
      @on-close="closeDelete"
    />
  </SettingsLayout>
</template>
